import { type FanShopProductCategory, useIntl } from '@capsicum/common';
import { CardCarousel, HeaderBlock, TOPBAR_HEIGHT, useRouter, useTutorial, useWindowSize } from '@capsicum/common-web';
import { type ChildrenCategoryMemoProps, type ParentCategory, useCategories } from '@capsicum/features/fanshop/shared';
import { ApprovedFooter, HeaderWarning } from '@capsicum/features/regulations/web';
import { useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import { useFanShopTutorial } from '../../hooks/useFanShopTutorial';
import { ProductListEmptyComponent } from '../../ui';
import { CategoryCard } from '../../ui/category-card/CategoryCard';

export const Rewards = () => {
	const { $t } = useIntl();
	const { spacings } = useTheme();
	const {
		dimensions: { width }
	} = useWindowSize();
	const { getCategoryBlurHash, getCategoryImage, isLoading, parentCategories } = useCategories();
	const router = useRouter();
	const { isOpen } = useTutorial();

	useFanShopTutorial(isLoading);

	const firstCardWrapperStyle = {
		transform: isOpen ? `translateX(-${spacings.xxsmall})` : 'initial',
		transition: 'transform 0.3s ease-in-out'
	};

	const ChildrenCategoryMemo = useCallback(
		({ id, listIndex, name, subcategory }: ChildrenCategoryMemoProps) => (
			<div>
				<HeaderBlock
					actionText={$t('global.seeAll')}
					isLoading={isLoading}
					testID={`fanshop-category-carousel-header-${id}`}
					title={name}
					onActionClick={() => router.push(`/fan-shop/rewards/categories/${id}`)}
				/>

				<S.StyledCardCarousel
					arrowsVisible
					cardWidth={368}
					testID={`fanshop-category-carousel-${id}`}
					variant="fullWidth"
				>
					{subcategory &&
						subcategory?.map((subCat: FanShopProductCategory, index: number) => {
							const isTutorialCard = index === 0 && listIndex === 1 && width < 400 && isOpen;

							return (
								<CategoryCard
									blurHash={getCategoryBlurHash(subCat)}
									data-tutorial={index === 0 && listIndex === 1 ? 'rewardsLobbyTutorial' : undefined}
									img={getCategoryImage(subCat)}
									isLoading={isLoading}
									key={subCat.id || `category-card-${listIndex}-${index}`}
									maxWidth={isTutorialCard ? 345 : 368}
									style={isTutorialCard ? firstCardWrapperStyle : {}}
									testID={`fanshop-category-carousel-card-${id}`}
									title={subCat.name!}
									onClick={() =>
										router.push(`/fan-shop/rewards/categories/${id}?selected=${subCat.id}`)
									}
								/>
							);
						})}
				</S.StyledCardCarousel>
			</div>
		),
		[$t, firstCardWrapperStyle, getCategoryBlurHash, getCategoryImage, isLoading, isOpen, router, width]
	);

	const parentCategoryMemo = useMemo(
		() =>
			(isLoading ? Array(4).fill({ children: [{}, {}, {}] }) : parentCategories)?.map(
				(parent: ParentCategory, index: number) => (
					<ChildrenCategoryMemo
						id={parent.id}
						key={index}
						listIndex={index}
						name={parent.name}
						subcategory={parent.children}
					/>
				)
			),
		[ChildrenCategoryMemo, parentCategories, isLoading]
	);

	return (
		<>
			<S.RewardsHeaderWarning horizontalPadding page="rewards" verticalPadding />
			<S.ContentWrapper>
				<S.CarouselsWrapper>{parentCategoryMemo}</S.CarouselsWrapper>
				{!parentCategories?.length && <ProductListEmptyComponent />}
				<ApprovedFooter page="rewards" />
			</S.ContentWrapper>
		</>
	);
};

namespace S {
	export const ContentWrapper = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		justify-content: flex-start;

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			padding: ${({ theme }) => `${theme.spacings.small}`};
		}

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			padding: ${({ theme }) => `${theme.spacings.medium}`};
		}

		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.xxlarge}`};
		}
	`;

	export const StyledCardCarousel = styled(CardCarousel)`
		margin-bottom: ${({ theme }) => theme.spacings.xxlarge};
	`;

	export const CarouselsWrapper = styled.div`
		display: flex;
		flex-direction: column;
	`;

	export const RewardsHeaderWarning = styled(HeaderWarning)`
		top: ${TOPBAR_HEIGHT}px;
	`;
}
